/**
 * Agent Dashboard — presentation helpers.
 *
 * Split out of agent_logs.ts to keep that file within the 300-line ceiling
 * (ST-01), matching the viewingDeck.ts / viewingDeck_render.ts convention.
 *
 * These take their data as arguments rather than reading module state, so
 * they are pure string builders and can be rendered in a test.
 */

import { esc } from '../../shared/rendering.js'
import type { LiveEvent } from './agent_logs_selectors.js'

export function renderObsSide(
  todos: { text: string; status: string; priority?: string }[],
  subs: { agent: string; task: string; model?: string }[],
  adv: { q: string; r?: string }[],
  events: readonly LiveEvent[],
): string {
  let h = ''

  // Task items
  if (todos.length > 0) {
    h += `<div class="run-card" style="border-left-color:var(--wrn);margin-bottom:8px;padding:8px 10px"><div class="run-card-head"><span class="rc-title" style="font-size:10px;color:var(--t3)">📋 Tasks</span></div>`
    h += todos
      .map(
        (t) =>
          `<div style="display:flex;align-items:center;gap:5px;padding:2px 0;font-size:10px;color:var(--${t.status === 'completed' || t.status === 'done' ? 't4' : 't2'});font-family:var(--mono)"><span style="color:var(--${t.status === 'completed' || t.status === 'done' ? 'grn' : 't4'})">${t.status === 'completed' || t.status === 'done' ? '✓' : '○'}</span><span style="${t.status === 'completed' || t.status === 'done' ? 'text-decoration:line-through' : ''}">${esc(t.text)}</span>${t.priority ? `<span style="margin-left:auto;font-size:8px;color:var(--${t.priority === 'high' || t.priority === 'urgent' ? 'err' : 't4'})">${t.priority}</span>` : ''}</div>`,
      )
      .join('')
    h += `</div>`
  }

  // Subagents
  if (subs.length > 0) {
    h += `<div class="run-card" style="border-left-color:var(--cyn);margin-bottom:8px;padding:8px 10px"><div class="run-card-head"><span class="rc-title" style="font-size:10px;color:var(--t3)">🔀 Agents</span></div>`
    h += subs
      .map(
        (s) =>
          `<div style="padding:3px 0;border-bottom:1px solid var(--bd);font-size:9px"><span style="color:var(--cyn);font-family:var(--mono)">${esc(s.agent)}</span>${s.model ? ` <span style="color:var(--t4);font-size:8px">${esc(s.model)}</span>` : ''}<div style="color:var(--t4);font-size:8px;margin-top:1px">${esc(s.task.slice(0, 100))}${s.task.length > 100 ? '...' : ''}</div></div>`,
      )
      .join('')
    h += `</div>`
  }

  // Advisor calls
  if (adv.length > 0) {
    h += `<div class="run-card" style="border-left-color:var(--pur);margin-bottom:8px;padding:8px 10px"><div class="run-card-head"><span class="rc-title" style="font-size:10px;color:var(--t3)">💡 Advise (${adv.length})</span><span style="margin-left:auto;font-size:8px;color:var(--t4);cursor:pointer;font-family:var(--mono)" onclick="var els=this.closest('.run-card').querySelectorAll('.adv-ans');var exp=els.length&&els[0].style.display!=='none';els.forEach(function(e){e.style.display=exp?'none':'block'});this.textContent=exp?'+ all':'− all'">+ all</span></div>`
    h += adv
      .map(
        (a) =>
          `<div style="padding:3px 0;border-bottom:1px solid var(--bd)"><div style="color:var(--pur);font-size:9px;font-family:var(--mono);cursor:pointer;display:flex;gap:3px" onclick="var a=this.nextElementSibling;a.style.display=a.style.display==='none'?'block':'none';this.querySelector('.adv-arrow').textContent=a.style.display==='none'?'▶':'▼'"><span class="adv-arrow">▶</span><span style="flex:1">${esc(a.q.slice(0, 150))}${a.q.length > 150 ? '...' : ''}</span></div>${a.r ? `<div class="adv-ans" style="display:none;color:var(--t2);font-size:9px;margin:4px 0 2px 14px;padding:6px 8px;background:var(--s1);border:1px solid var(--bd);border-radius:4px;line-height:1.4;max-height:200px;overflow-y:auto">${esc(a.r)}</div>` : '<div class="adv-ans" style="display:none;color:var(--t4);font-size:8px;margin:4px 0 2px 14px">Waiting...</div>'}</div>`,
      )
      .join('')
    h += `</div>`
  }

  // Current thinking
  const lt = [...events].reverse().find((e) => e.type === 'think' && e.think)
  if (lt?.think) {
    h += `<div class="run-card" style="border-left-color:var(--pur);margin-bottom:8px;padding:8px 10px"><div class="run-card-head"><span class="rc-title" style="font-size:10px;color:var(--t3)">🧠 Thinking</span></div><pre style="margin:0;font-family:var(--mono);font-size:9px;color:var(--t3);white-space:pre-wrap;max-height:150px;overflow-y:auto">${esc(lt.think.slice(-1500))}</pre></div>`
  }

  return (
    h ||
    '<div class="empty" style="font-size:10px;color:var(--t4);padding:8px">No activity yet</div>'
  )
}

export function renderEventCards(events: readonly LiveEvent[], eventFilter: string | null): string {
  const filtered = getFiltered(events, eventFilter)
  let thinkBuf = ''
  let textBuf = ''
  let int = false
  let intx = false
  let html = ''

  function ft() {
    if (!thinkBuf.trim()) {
      thinkBuf = ''
      int = false
      return
    }
    html += `<div class="ev" style="border-left:2px solid var(--pur)"><div class="ev-h"><span class="ev-icon thinking">🧠</span><span class="ev-stage impl" style="background:var(--pur-d);color:var(--pur)">Thinking</span></div><div class="ev-body"><pre style="margin:0;font-family:var(--mono);font-size:11px;color:var(--t3);white-space:pre-wrap;max-height:250px;overflow-y:auto">${esc(thinkBuf)}</pre></div></div>`
    thinkBuf = ''
    int = false
  }
  function fx() {
    if (!textBuf.trim()) {
      textBuf = ''
      intx = false
      return
    }
    html += `<div class="ev"><div class="ev-body">${esc(textBuf)}</div></div>`
    textBuf = ''
    intx = false
  }

  for (const e of filtered) {
    if (e.type === 'turn') {
      ft()
      fx()
      html += `<div class="ev" style="background:var(--s2);border-color:var(--cyn)"><div class="ev-h"><span class="ev-icon" style="color:var(--cyn)">▶</span><span class="ev-stage impl" style="background:var(--cyn-d);color:var(--cyn)">Turn ${e.turn || 0}</span></div></div>`
    } else if (e.type === 'think') {
      if (intx) fx()
      thinkBuf += e.think || ''
      int = true
    } else if (e.type === 'think_end') {
      ft()
    } else if (e.type === 'text') {
      if (int) ft()
      textBuf += e.text || ''
      intx = true
    } else if (e.type === 'tool') {
      ft()
      fx()
      const isAdv = e.tool === 'advise'
      const isSub = e.tool === 'subagent'
      const clr = isAdv ? 'var(--pur)' : 'var(--cyn)'
      const icon = isAdv ? '💡' : isSub ? '🔀' : '⚒'
      let ap = e.toolArgs || ''
      if (isAdv)
        try {
          const a = JSON.parse(e.toolArgs || '{}')
          ap = ((a.question as string) || '').slice(0, 200)
        } catch {
          /* */
        }
      if (isSub)
        try {
          const a = JSON.parse(e.toolArgs || '{}')
          ap = `${(a.tasks as { agent: string }[] | undefined)?.map((t) => t.agent).join(', ') || a.agent || '?'}`
        } catch {
          /* */
        }
      html += `<div class="ev" style="border-left:2px solid ${clr}"><div class="ev-h"><span class="ev-icon">${icon}</span><span class="ev-stage impl" style="background:var(--${isAdv ? 'pur' : 'cyn'}-d);color:${clr}">${esc(e.tool || '?')}</span></div><div class="ev-body"><code>${esc(ap)}</code></div></div>`
    } else if (e.type === 'result') {
      if (!e.toolResult) continue
      const clr = e.toolError ? 'var(--err)' : 'var(--grn)'
      html += `<div class="ev" style="margin-left:16px;border-left:2px solid ${clr}"><div class="ev-body"><span style="color:${clr}">${e.toolError ? '✗' : '✓'}</span> <code>${esc(e.toolResult.slice(0, 300))}</code></div></div>`
    } else if (e.type === 'usage') {
      ft()
      fx()
      html += `<div style="text-align:right;font-family:var(--mono);font-size:10px;color:var(--t4);padding:2px 8px">${e.usage || ''}</div>`
    } else if (e.type === 'agent_start')
      html += `<div class="ev" style="border-left:3px solid var(--grn)"><div class="ev-h"><span class="ev-icon" style="color:var(--grn)">▶</span><span class="ev-stage impl" style="background:var(--grn-d);color:var(--grn)">Agent started</span></div></div>`
    else if (e.type === 'agent_end')
      html += `<div class="ev" style="border-left:3px solid var(--grn)"><div class="ev-h"><span class="ev-icon" style="color:var(--grn)">■</span><span class="ev-stage impl" style="background:var(--grn-d);color:var(--grn)">Agent ended</span></div></div>`
  }
  ft()
  fx()
  return html
}

export function getFiltered(
  events: readonly LiveEvent[],
  eventFilter: string | null,
): readonly LiveEvent[] {
  if (!eventFilter) return events
  return events.filter((e) => {
    if (eventFilter === 'think') return e.type === 'think' || e.type === 'think_end'
    if (eventFilter === 'text') return e.type === 'text'
    if (eventFilter === 'tool') return e.type === 'tool' || e.type === 'result'
    if (eventFilter === 'advise') return e.type === 'tool' && e.tool === 'advise'
    if (eventFilter === 'subagent') return e.type === 'tool' && e.tool === 'subagent'
    return true
  })
}

export function fmt(n: number): string {
  if (n < 1000) return String(n)
  if (n < 10000) return `${(n / 1000).toFixed(1)}k`
  return `${Math.round(n / 1000)}k`
}
